import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { reduxForm } from 'redux-form';
import omit from 'lodash/omit';
import isArray from 'lodash/isArray';
import Axios from 'axios';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { showToaster } from 'nallian-shared-ui/lib/lib/showToaster';
import { FormUi, Grid, Field, Flex, Button } from 'nallian-shared-ui/lib/components';
import { BYTES_IN_A_MEGABYTE } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui/FileFieldValueComponent';
import { FileFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { generateErrorsForRequiredFields } from 'nallian-shared-ui/lib/lib/validate';
import apiRoutes from '../../../../app/api/routes';
import { setValue } from '../../../../app/helpers/setValue';
import { ORGANISATION_RESOURCE } from '../../constants';

const FORM_NAME = 'organisationLogoForm';
const REQUIRED_FIELDS = ['file'];

const allowedFileTypes = ['.png'];

const HeightCappedGridColumn = styled(Grid.Column)`
  .field > div {
    max-height: 155px;
  }
`;

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  return errors;
};

const OrganisationLogoForm = ({ handleSubmit, organisation }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [uploading, setUploading] = useState(false);

  const onSubmit = useCallback(
    body => {
      setUploading(true);
      const r = new Blob([JSON.stringify(omit(body, ['file']))], {
        type: 'application/json',
      });
      const formData = new FormData();
      formData.append('request', r, '');
      formData.append('file', body.file);

      Axios.put(apiRoutes.organisationLogoV2(organisation.id), formData, {
        headers: { 'content-type': 'multipart/form-data' },
      })
        .then(response => {
          setUploading(false);
          dispatch(setValue(ORGANISATION_RESOURCE, { ...organisation, logoUrl: response.data }));
          showToaster('success', t('uploadLogoSuccess'));
        })
        .catch(err => {
          let errorMessage = '';

          if (err?.response?.statusCode === 403) {
            errorMessage = t('unauthorized');
          } else if (isArray(err?.response?.data)) {
            errorMessage = err?.response?.data.map(c => (c.errors || []).join(' ')).join(' ');
          }

          setUploading(false);
          showToaster('error', errorMessage || t('uploadLogoError'));
        });
    },
    [t, setUploading, dispatch, organisation]
  );

  return (
    <FormUi fluid onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <HeightCappedGridColumn width="16">
          <Field
            fluid
            label={t('uploadANewLogo')}
            name="file"
            accept={allowedFileTypes}
            valueComponent={FileFieldValueComponent}
            disabled={uploading}
            maxSize={BYTES_IN_A_MEGABYTE * 2}
          />
        </HeightCappedGridColumn>
        <Flex fluid justifyContent="flex-end">
          <Button
            btnprimary
            onClick={handleSubmit(onSubmit)}
            content={t('uploadLogo')}
            loading={uploading}
            disabled={uploading}
            icon="upload"
          />
        </Flex>
      </Grid>
    </FormUi>
  );
};

OrganisationLogoForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  organisation: PropTypes.object.isRequired,
};

export default reduxForm({
  validate,
  form: FORM_NAME,
  enableReinitialize: true,
})(OrganisationLogoForm);
